import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Button, Card, Plus, TextInput, Trash01 } from '@opendate-app/setlist';

const PIXEL_ID_FIELD = { field: 'pixel_id', label: 'Pixel ID', placeholder: 'o8z2x' };

const EVENT_ID_FIELDS = [
  { field: 'purchase_event_id', label: 'Purchase event ID', placeholder: 'tw-o8z2x-o8z5c' },
  { field: 'add_to_cart_event_id', label: 'Add to cart event ID', placeholder: 'tw-o8z2x-o8z5d' },
  {
    field: 'checkout_event_id',
    label: 'Checkout initiated event ID',
    placeholder: 'tw-o8z2x-o8z5e',
    hint: 'Create this as a Custom event in X.',
  },
];

const X_PIXEL_FIELDS = [PIXEL_ID_FIELD, ...EVENT_ID_FIELDS];

let lastDraftKey = 0;

const nextDraftKey = () => {
  lastDraftKey += 1;
  return lastDraftKey;
};

const emptyXPixel = () => ({
  ...Object.fromEntries(X_PIXEL_FIELDS.map(({ field }) => [field, ''])),
  draftKey: nextDraftKey(),
});

const toDraft = (pixel) => ({ ...emptyXPixel(), ...pixel });

const savableXPixels = (draftPixels) => draftPixels
  .map((pixel) => Object.fromEntries(
    X_PIXEL_FIELDS.map(({ field }) => [field, (pixel[field] || '').trim()])
  ))
  .filter((pixel) => pixel.pixel_id !== '');

const XMarketingPixelsSettings = ({ xMarketingPixels, setXMarketingPixels }) => {
  const [draftPixels, setDraftPixels] = useState(() => xMarketingPixels.map(toDraft));

  const commit = (pixels) => {
    const savable = savableXPixels(pixels);
    if (JSON.stringify(savable) === JSON.stringify(savableXPixels(xMarketingPixels))) return;

    setXMarketingPixels(savable);
  };

  const updateField = (index, field, value) => {
    setDraftPixels(draftPixels.map((pixel, pixelIndex) => (
      pixelIndex === index ? { ...pixel, [field]: value } : pixel
    )));
  };

  const removePixel = (index) => {
    const remaining = draftPixels.filter((_pixel, pixelIndex) => pixelIndex !== index);
    setDraftPixels(remaining);
    commit(remaining);
  };

  return (
    <>
      {draftPixels.map((pixel, index) => {
        const renderField = ({ field, label, placeholder, hint }) => (
          <TextInput
            id={`x-marketing-pixel-${index}-${field}`}
            label={label}
            placeholder={placeholder}
            hint={hint}
            value={pixel[field]}
            onChange={(e) => updateField(index, field, e.target.value)}
            onBlur={() => commit(draftPixels)}
          />
        );

        return (
          <div key={pixel.draftKey} style={{ marginBottom: 'var(--setlist-spacing-xl)' }}>
            <Card padding="md">
              <div className="d-flex align-items-start justify-content-between" style={{ gap: 'var(--setlist-spacing-xl)' }}>
                <div style={{ flex: '0 1 320px' }}>
                  {renderField(PIXEL_ID_FIELD)}
                </div>
                <Button
                  variant="tertiary"
                  size="sm"
                  icon={Trash01}
                  aria-label="Remove X pixel"
                  onClick={() => removePixel(index)}
                />
              </div>
              <div className="row" style={{ marginTop: 'var(--setlist-spacing-xl)' }}>
                {EVENT_ID_FIELDS.map((eventField) => (
                  <div className="col-12 col-md-4" key={eventField.field}>
                    {renderField(eventField)}
                  </div>
                ))}
              </div>
            </Card>
          </div>
        );
      })}
      <Button
        variant="link-color"
        size="sm"
        icon={Plus}
        onClick={() => setDraftPixels([...draftPixels, emptyXPixel()])}
      >
        Add X Pixel
      </Button>
    </>
  );
};

XMarketingPixelsSettings.propTypes = {
  xMarketingPixels: PropTypes.arrayOf(PropTypes.shape({
    pixel_id: PropTypes.string,
    purchase_event_id: PropTypes.string,
    add_to_cart_event_id: PropTypes.string,
    checkout_event_id: PropTypes.string,
  })).isRequired,
  setXMarketingPixels: PropTypes.func.isRequired,
};

export default XMarketingPixelsSettings;
